/**
 * Phase 8 · Cluster C-47 — shared "you don't have access" state.
 *
 * Rendered by the team member profile page (and any future page that
 * trips an `RbacDenied`) inside a single top-level catch block.
 *
 * Keeps copy intentionally generic so the same component works for
 * the three distinct denial reasons the spec covers:
 *   - peer views with open_profiles=false
 *   - senior_recruiter viewing someone outside their subtree
 *   - viewer/admin looking at a member in another org (edge case)
 */
import { Link } from 'react-router-dom';
import { Lock, ArrowLeft } from 'lucide-react';

interface AccessDeniedStateProps {
  /** Override the default "you don't have permission" copy. */
  message?: string;
  /** Link target for the back action. Defaults to /team. */
  backHref?: string;
  backLabel?: string;
}

export function AccessDeniedState({
  message,
  backHref = '/team',
  backLabel = 'Back to team',
}: AccessDeniedStateProps) {
  return (
    <div
      role="alert"
      className="mx-auto flex max-w-md flex-col items-center justify-center rounded-xl border vscode-border vscode-bg-secondary px-6 py-10 text-center"
    >
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300">
        <Lock className="h-5 w-5" aria-hidden />
      </div>
      <h2 className="text-sm font-semibold vscode-text-primary">
        You don&rsquo;t have access to this profile
      </h2>
      <p className="mt-1 max-w-sm text-[12px] vscode-text-tertiary">
        {message ??
          'This member is outside your reporting scope and your organisation has private profiles enabled. Ask an admin for access.'}
      </p>
      <Link
        to={backHref}
        className="mt-4 inline-flex items-center gap-1 rounded-[8px] border px-3 py-1.5 text-[12px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-text-primary hover:vscode-bg-hover"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
        {backLabel}
      </Link>
    </div>
  );
}
